import AsyncStorage from '@react-native-async-storage/async-storage';

/**
 * Unified Job Application Service
 * Centralized data management for all job application related screens
 * Handles data synchronization, storage, and business logic
 */
class UnifiedJobApplicationService {
  // Storage keys
  static STORAGE_KEYS = {
    APPLICATIONS: 'unified_job_applications',
    TRACKING_DATA: 'application_tracking_data',
    NOTIFICATIONS: 'application_notifications',
    ANALYTICS: 'application_analytics',
    USER_PROFILE: 'user_application_profile',
    JOBS: 'available_jobs',
    DOCUMENTS: 'application_documents'
  };

  // Application status constants
  static APPLICATION_STATUS = {
    DRAFT: 'draft',
    SUBMITTED: 'submitted',
    RECEIVED: 'received',
    UNDER_REVIEW: 'under_review',
    CONSIDERED: 'considered',
    TESTS_COMPETENCIES: 'tests_competencies',
    INTERVIEW_SCHEDULED: 'interview_scheduled',
    INTERVIEW_COMPLETED: 'interview_completed',
    OFFER_LETTER: 'offer_letter',
    FINAL_APPROVAL: 'final_approval',
    HIRED: 'hired',
    REJECTED: 'rejected',
    WITHDRAWN: 'withdrawn'
  };

  // Status configuration for UI display
  static STATUS_CONFIG = {
    [this.APPLICATION_STATUS.DRAFT]: {
      label: 'Draft',
      icon: 'document-outline',
      color: '#95a5a6',
      progress: 0,
      description: 'Application in progress',
      nextAction: 'Complete and submit application',
      actionScreen: 'JobApplication'
    },
    [this.APPLICATION_STATUS.SUBMITTED]: {
      label: 'Submitted',
      icon: 'paper-plane',
      color: '#3498db',
      progress: 10,
      description: 'Application successfully submitted',
      nextAction: 'Waiting for company response',
      actionScreen: null
    },
    [this.APPLICATION_STATUS.RECEIVED]: {
      label: 'Received',
      icon: 'checkmark-circle',
      color: '#2ecc71',
      progress: 20,
      description: 'Application received by company',
      nextAction: 'Under initial review',
      actionScreen: null
    },
    [this.APPLICATION_STATUS.UNDER_REVIEW]: {
      label: 'Under Review',
      icon: 'eye',
      color: '#f39c12',
      progress: 35,
      description: 'Being reviewed by hiring team',
      nextAction: 'Awaiting review decision',
      actionScreen: null
    },
    [this.APPLICATION_STATUS.CONSIDERED]: {
      label: 'Considered',
      icon: 'star',
      color: '#9b59b6',
      progress: 50,
      description: 'Being considered for position',
      nextAction: 'May receive assessment or interview',
      actionScreen: null
    },
    [this.APPLICATION_STATUS.TESTS_COMPETENCIES]: {
      label: 'Tests & Assessments',
      icon: 'school',
      color: '#e74c3c',
      progress: 65,
      description: 'Complete assessments and tests',
      nextAction: 'Take assessment',
      actionScreen: 'ActionScreen'
    },
    [this.APPLICATION_STATUS.INTERVIEW_SCHEDULED]: {
      label: 'Interview Scheduled',
      icon: 'calendar',
      color: '#1abc9c',
      progress: 75,
      description: 'Interview has been scheduled',
      nextAction: 'Prepare for interview',
      actionScreen: 'ActionScreen'
    },
    [this.APPLICATION_STATUS.INTERVIEW_COMPLETED]: {
      label: 'Interview Completed',
      icon: 'people',
      color: '#16a085',
      progress: 85,
      description: 'Interview successfully completed',
      nextAction: 'Awaiting final decision',
      actionScreen: null
    },
    [this.APPLICATION_STATUS.OFFER_LETTER]: {
      label: 'Job Offer',
      icon: 'document-text',
      color: '#27ae60',
      progress: 95,
      description: 'Job offer received',
      nextAction: 'Review and respond to offer',
      actionScreen: 'ActionScreen'
    },
    [this.APPLICATION_STATUS.FINAL_APPROVAL]: {
      label: 'Final Approval',
      icon: 'thumbs-up',
      color: '#2980b9',
      progress: 98,
      description: 'Offer pending final approval',
      nextAction: 'Awaiting final confirmation',
      actionScreen: null
    },
    [this.APPLICATION_STATUS.HIRED]: {
      label: 'Hired',
      icon: 'trophy',
      color: '#16a085',
      progress: 100,
      description: 'Congratulations! You are hired',
      nextAction: 'Prepare for onboarding',
      actionScreen: 'ActionScreen'
    },
    [this.APPLICATION_STATUS.REJECTED]: {
      label: 'Not Selected',
      icon: 'close-circle',
      color: '#e74c3c',
      progress: 0,
      description: 'Application was not successful',
      nextAction: 'Continue job search',
      actionScreen: null
    },
    [this.APPLICATION_STATUS.WITHDRAWN]: {
      label: 'Withdrawn',
      icon: 'remove-circle',
      color: '#95a5a6',
      progress: 0,
      description: 'Application withdrawn',
      nextAction: 'Application closed',
      actionScreen: null
    }
  };

  /**
   * Initialize the service and perform any necessary setup
   */
  static async initialize() {
    try {
      console.log('🔧 Initializing UnifiedJobApplicationService...');
      
      // Ensure all storage keys exist
      for (const [key, storageKey] of Object.entries(this.STORAGE_KEYS)) {
        const data = await AsyncStorage.getItem(storageKey);
        if (!data) {
          await AsyncStorage.setItem(storageKey, JSON.stringify([]));
        }
      }
      
      console.log('✅ UnifiedJobApplicationService initialized successfully');
      return true;
    } catch (error) {
      console.error('❌ Error initializing UnifiedJobApplicationService:', error);
      return false;
    }
  }

  /**
   * Create a new job application
   */
  static async createApplication(applicationData) {
    try {
      const applicationId = `app_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      const newApplication = {
        id: applicationId,
        jobId: applicationData.jobId,
        jobTitle: applicationData.jobTitle,
        company: applicationData.company,
        location: applicationData.location,
        salary: applicationData.salary,
        jobType: applicationData.jobType,
        
        // Application details
        status: this.APPLICATION_STATUS.DRAFT,
        progress: 0,
        applicationDate: new Date().toISOString(),
        lastUpdated: new Date().toISOString(),
        
        // Applicant information
        applicantData: {
          firstName: applicationData.firstName,
          lastName: applicationData.lastName,
          email: applicationData.email,
          phone: applicationData.phone,
          currentJobTitle: applicationData.currentJobTitle,
          experience: applicationData.experience,
          education: applicationData.education,
          skills: applicationData.skills,
          coverLetter: applicationData.coverLetter,
          salaryExpectations: applicationData.salaryExpectations,
          availability: applicationData.availability,
          portfolioUrl: applicationData.portfolioUrl,
          linkedinUrl: applicationData.linkedinUrl
        },
        
        // Documents
        documents: applicationData.documents || [],
        
        // Tracking information
        statusHistory: [{
          status: this.APPLICATION_STATUS.DRAFT,
          date: new Date().toISOString(),
          note: 'Application created'
        }],
        
        // Additional metadata
        matchScore: applicationData.matchScore || 0,
        isRemote: applicationData.isRemote || false,
        canWithdraw: true,
        requiresAction: false,
        
        // Assessment and interview data (initially empty)
        assessments: [],
        interviews: [],
        offers: [],
        
        // Notification settings
        notificationsEnabled: true
      };

      // Save to storage
      const applications = await this.getAllApplications();
      applications.push(newApplication);
      await AsyncStorage.setItem(this.STORAGE_KEYS.APPLICATIONS, JSON.stringify(applications));
      
      // Create notification
      await this.createNotification({
        type: 'application_created',
        title: 'Application Draft Created',
        message: `Draft application for ${applicationData.jobTitle} at ${applicationData.company} has been created`,
        applicationId: applicationId,
        priority: 'normal'
      });
      
      console.log('✅ Application created:', applicationId);
      return newApplication;
    } catch (error) {
      console.error('❌ Error creating application:', error);
      throw error;
    }
  }

  /**
   * Submit an application (change status from draft to submitted)
   */
  static async submitApplication(applicationId, finalData = {}) {
    try {
      const applications = await this.getAllApplications();
      const applicationIndex = applications.findIndex(app => app.id === applicationId);
      
      if (applicationIndex === -1) {
        throw new Error('Application not found');
      }
      
      const application = applications[applicationIndex];
      
      // Update application with final data
      application.status = this.APPLICATION_STATUS.SUBMITTED;
      application.progress = this.STATUS_CONFIG[this.APPLICATION_STATUS.SUBMITTED].progress;
      application.lastUpdated = new Date().toISOString();
      application.submissionDate = new Date().toISOString();
      
      // Merge any final data
      if (finalData.applicantData) {
        application.applicantData = { ...application.applicantData, ...finalData.applicantData };
      }
      if (finalData.documents) {
        application.documents = finalData.documents;
      }
      
      // Add to status history
      application.statusHistory.push({
        status: this.APPLICATION_STATUS.SUBMITTED,
        date: new Date().toISOString(),
        note: 'Application submitted successfully'
      });
      
      // Update in storage
      applications[applicationIndex] = application;
      await AsyncStorage.setItem(this.STORAGE_KEYS.APPLICATIONS, JSON.stringify(applications));
      
      // Create notification
      await this.createNotification({
        type: 'application_submitted',
        title: 'Application Submitted',
        message: `Your application for ${application.jobTitle} at ${application.company} has been submitted successfully`,
        applicationId: applicationId,
        priority: 'high'
      });
      
      // Update analytics
      await this.updateAnalytics();
      
      console.log('✅ Application submitted:', applicationId);
      return application;
    } catch (error) {
      console.error('❌ Error submitting application:', error);
      throw error;
    }
  }

  /**
   * Update application status
   */
  static async updateApplicationStatus(applicationId, newStatus, notes = '', additionalData = {}) {
    try {
      const applications = await this.getAllApplications();
      const applicationIndex = applications.findIndex(app => app.id === applicationId);
      
      if (applicationIndex === -1) {
        throw new Error('Application not found');
      }
      
      const application = applications[applicationIndex];
      const oldStatus = application.status;
      
      // Update application
      application.status = newStatus;
      application.progress = this.STATUS_CONFIG[newStatus]?.progress || 0;
      application.lastUpdated = new Date().toISOString();
      
      // Add status-specific data
      if (newStatus === this.APPLICATION_STATUS.INTERVIEW_SCHEDULED && additionalData.interviewDate) {
        application.interviewDate = additionalData.interviewDate;
        application.interviewType = additionalData.interviewType;
        application.interviewLocation = additionalData.interviewLocation;
        application.requiresAction = true;
      }
      
      if (newStatus === this.APPLICATION_STATUS.OFFER_LETTER && additionalData.offerDetails) {
        application.offerDetails = additionalData.offerDetails;
        application.requiresAction = true;
      }
      
      // Add to status history
      application.statusHistory.push({
        status: newStatus,
        date: new Date().toISOString(),
        note: notes || this.STATUS_CONFIG[newStatus]?.description || 'Status updated'
      });
      
      // Update in storage
      applications[applicationIndex] = application;
      await AsyncStorage.setItem(this.STORAGE_KEYS.APPLICATIONS, JSON.stringify(applications));
      
      // Create notification
      await this.createNotification({
        type: 'status_update',
        title: `${application.jobTitle} - ${this.STATUS_CONFIG[newStatus]?.label}`,
        message: notes || `Your application status has been updated to: ${this.STATUS_CONFIG[newStatus]?.description}`,
        applicationId: applicationId,
        priority: this.getNotificationPriority(newStatus),
        actionScreen: this.STATUS_CONFIG[newStatus]?.actionScreen
      });
      
      // Update analytics
      await this.updateAnalytics();
      
      console.log('✅ Application status updated:', applicationId, oldStatus, '→', newStatus);
      return application;
    } catch (error) {
      console.error('❌ Error updating application status:', error);
      throw error;
    }
  }

  /**
   * Get all applications
   */
  static async getAllApplications() {
    try {
      const data = await AsyncStorage.getItem(this.STORAGE_KEYS.APPLICATIONS);
      return data ? JSON.parse(data) : [];
    } catch (error) {
      console.error('❌ Error getting applications:', error);
      return [];
    }
  }

  /**
   * Get application by ID
   */
  static async getApplicationById(applicationId) {
    try {
      const applications = await this.getAllApplications();
      return applications.find(app => app.id === applicationId) || null;
    } catch (error) {
      console.error('❌ Error getting application by ID:', error);
      return null;
    }
  }

  /**
   * Get applications by status
   */
  static async getApplicationsByStatus(status) {
    try {
      const applications = await this.getAllApplications();
      return applications.filter(app => app.status === status);
    } catch (error) {
      console.error('❌ Error getting applications by status:', error);
      return [];
    }
  }

  /**
   * Delete application
   */
  static async deleteApplication(applicationId) {
    try {
      const applications = await this.getAllApplications();
      const filteredApplications = applications.filter(app => app.id !== applicationId);
      
      await AsyncStorage.setItem(this.STORAGE_KEYS.APPLICATIONS, JSON.stringify(filteredApplications));
      
      // Create notification
      await this.createNotification({
        type: 'application_deleted',
        title: 'Application Deleted',
        message: 'An application has been removed from your tracking list',
        priority: 'normal'
      });
      
      // Update analytics
      await this.updateAnalytics();
      
      console.log('✅ Application deleted:', applicationId);
      return true;
    } catch (error) {
      console.error('❌ Error deleting application:', error);
      return false;
    }
  }

  /**
   * Create notification
   */
  static async createNotification(notificationData) {
    try {
      const notification = {
        id: `notif_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        type: notificationData.type,
        title: notificationData.title,
        message: notificationData.message,
        applicationId: notificationData.applicationId || null,
        timestamp: new Date().toISOString(),
        read: false,
        priority: notificationData.priority || 'normal',
        actionScreen: notificationData.actionScreen || null,
        data: notificationData.data || {}
      };

      const notifications = await this.getNotifications();
      notifications.unshift(notification);
      
      // Keep only last 100 notifications
      if (notifications.length > 100) {
        notifications.splice(100);
      }
      
      await AsyncStorage.setItem(this.STORAGE_KEYS.NOTIFICATIONS, JSON.stringify(notifications));
      
      console.log('🔔 Notification created:', notification.title);
      return notification;
    } catch (error) {
      console.error('❌ Error creating notification:', error);
      return null;
    }
  }

  /**
   * Get all notifications
   */
  static async getNotifications() {
    try {
      const data = await AsyncStorage.getItem(this.STORAGE_KEYS.NOTIFICATIONS);
      return data ? JSON.parse(data) : [];
    } catch (error) {
      console.error('❌ Error getting notifications:', error);
      return [];
    }
  }

  /**
   * Mark notification as read
   */
  static async markNotificationAsRead(notificationId) {
    try {
      const notifications = await this.getNotifications();
      const notification = notifications.find(n => n.id === notificationId);
      
      if (notification) {
        notification.read = true;
        await AsyncStorage.setItem(this.STORAGE_KEYS.NOTIFICATIONS, JSON.stringify(notifications));
      }
      
      return true;
    } catch (error) {
      console.error('❌ Error marking notification as read:', error);
      return false;
    }
  }

  /**
   * Get notification priority based on status
   */
  static getNotificationPriority(status) {
    const highPriorityStatuses = [
      this.APPLICATION_STATUS.TESTS_COMPETENCIES,
      this.APPLICATION_STATUS.INTERVIEW_SCHEDULED,
      this.APPLICATION_STATUS.OFFER_LETTER,
      this.APPLICATION_STATUS.HIRED,
      this.APPLICATION_STATUS.REJECTED
    ];
    
    return highPriorityStatuses.includes(status) ? 'high' : 'normal';
  }

  /**
   * Update analytics
   */
  static async updateAnalytics() {
    try {
      const applications = await this.getAllApplications();
      
      const analytics = {
        totalApplications: applications.length,
        activeApplications: applications.filter(app => 
          ![this.APPLICATION_STATUS.HIRED, this.APPLICATION_STATUS.REJECTED, this.APPLICATION_STATUS.WITHDRAWN].includes(app.status)
        ).length,
        submittedApplications: applications.filter(app => app.status !== this.APPLICATION_STATUS.DRAFT).length,
        interviewApplications: applications.filter(app => 
          [this.APPLICATION_STATUS.INTERVIEW_SCHEDULED, this.APPLICATION_STATUS.INTERVIEW_COMPLETED].includes(app.status)
        ).length,
        offerApplications: applications.filter(app => 
          [this.APPLICATION_STATUS.OFFER_LETTER, this.APPLICATION_STATUS.FINAL_APPROVAL].includes(app.status)
        ).length,
        hiredApplications: applications.filter(app => app.status === this.APPLICATION_STATUS.HIRED).length,
        rejectedApplications: applications.filter(app => app.status === this.APPLICATION_STATUS.REJECTED).length,
        successRate: applications.length > 0 ? 
          (applications.filter(app => app.status === this.APPLICATION_STATUS.HIRED).length / applications.length * 100).toFixed(1) : 0,
        averageTimeToInterview: this.calculateAverageTimeToInterview(applications),
        statusDistribution: this.calculateStatusDistribution(applications),
        lastUpdated: new Date().toISOString()
      };

      await AsyncStorage.setItem(this.STORAGE_KEYS.ANALYTICS, JSON.stringify(analytics));
      
      console.log('📊 Analytics updated');
      return analytics;
    } catch (error) {
      console.error('❌ Error updating analytics:', error);
      return null;
    }
  }

  /**
   * Get analytics data
   */
  static async getAnalytics() {
    try {
      const data = await AsyncStorage.getItem(this.STORAGE_KEYS.ANALYTICS);
      return data ? JSON.parse(data) : null;
    } catch (error) {
      console.error('❌ Error getting analytics:', error);
      return null;
    }
  }

  /**
   * Calculate average time to interview
   */
  static calculateAverageTimeToInterview(applications) {
    const interviewApps = applications.filter(app => 
      app.interviewDate && app.submissionDate
    );
    
    if (interviewApps.length === 0) return 0;
    
    const totalDays = interviewApps.reduce((sum, app) => {
      const submissionDate = new Date(app.submissionDate);
      const interviewDate = new Date(app.interviewDate);
      const daysDiff = Math.ceil((interviewDate - submissionDate) / (1000 * 60 * 60 * 24));
      return sum + daysDiff;
    }, 0);
    
    return Math.round(totalDays / interviewApps.length);
  }

  /**
   * Calculate status distribution
   */
  static calculateStatusDistribution(applications) {
    const distribution = {};
    
    Object.values(this.APPLICATION_STATUS).forEach(status => {
      distribution[status] = applications.filter(app => app.status === status).length;
    });
    
    return distribution;
  }

  /**
   * Sync with legacy data structures (for backward compatibility)
   */
  static async syncWithLegacyData() {
    try {
      console.log('🔄 Syncing with legacy data structures...');
      
      // Sync with JobApplicationsScreen data
      const legacyJobApplications = await AsyncStorage.getItem('jobApplications');
      if (legacyJobApplications) {
        const legacyApps = JSON.parse(legacyJobApplications);
        const currentApps = await this.getAllApplications();
        
        // Convert legacy applications to new format
        for (const legacyApp of legacyApps) {
          const existingApp = currentApps.find(app => app.id === legacyApp.id);
          if (!existingApp) {
            await this.createApplicationFromLegacyData(legacyApp);
          }
        }
      }
      
      // Sync with ApplicationsTrackingScreen data
      const trackingData = await AsyncStorage.getItem('trackingApplications');
      if (trackingData) {
        const trackingApps = JSON.parse(trackingData);
        // Update existing applications with tracking data
        await this.mergeTrackingData(trackingApps);
      }
      
      console.log('✅ Legacy data sync completed');
      return true;
    } catch (error) {
      console.error('❌ Error syncing legacy data:', error);
      return false;
    }
  }

  /**
   * Create application from legacy data
   */
  static async createApplicationFromLegacyData(legacyApp) {
    try {
      const applicationData = {
        jobId: legacyApp.jobId || legacyApp.id,
        jobTitle: legacyApp.jobTitle || legacyApp.title,
        company: legacyApp.company,
        location: legacyApp.location,
        salary: legacyApp.salary,
        jobType: legacyApp.jobType || 'Full-time',
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        coverLetter: '',
        documents: [],
        matchScore: legacyApp.matchScore || 0,
        isRemote: legacyApp.isRemote || false
      };
      
      const newApp = await this.createApplication(applicationData);
      
      // Update status if provided
      if (legacyApp.status && legacyApp.status !== this.APPLICATION_STATUS.DRAFT) {
        await this.updateApplicationStatus(newApp.id, legacyApp.status, 'Migrated from legacy data');
      }
      
      return newApp;
    } catch (error) {
      console.error('❌ Error creating application from legacy data:', error);
      return null;
    }
  }

  /**
   * Merge tracking data with existing applications
   */
  static async mergeTrackingData(trackingApps) {
    try {
      const applications = await this.getAllApplications();
      let updated = false;
      
      for (const trackingApp of trackingApps) {
        const existingApp = applications.find(app => 
          app.id === trackingApp.id || 
          (app.jobId === trackingApp.jobId && app.company === trackingApp.company)
        );
        
        if (existingApp) {
          // Merge tracking data
          existingApp.assessments = trackingApp.assessments || existingApp.assessments;
          existingApp.interviews = trackingApp.interviews || existingApp.interviews;
          existingApp.statusHistory = trackingApp.statusHistory || existingApp.statusHistory;
          existingApp.lastUpdated = new Date().toISOString();
          updated = true;
        }
      }
      
      if (updated) {
        await AsyncStorage.setItem(this.STORAGE_KEYS.APPLICATIONS, JSON.stringify(applications));
      }
      
      return true;
    } catch (error) {
      console.error('❌ Error merging tracking data:', error);
      return false;
    }
  }

  /**
   * Export applications data (for backup or transfer)
   */
  static async exportApplicationsData() {
    try {
      const applications = await this.getAllApplications();
      const notifications = await this.getNotifications();
      const analytics = await this.getAnalytics();
      
      const exportData = {
        applications,
        notifications,
        analytics,
        exportDate: new Date().toISOString(),
        version: '1.0'
      };
      
      return exportData;
    } catch (error) {
      console.error('❌ Error exporting applications data:', error);
      return null;
    }
  }

  /**
   * Import applications data (from backup or transfer)
   */
  static async importApplicationsData(importData) {
    try {
      if (!importData || !importData.applications) {
        throw new Error('Invalid import data');
      }
      
      // Backup current data
      const currentData = await this.exportApplicationsData();
      await AsyncStorage.setItem('backup_before_import', JSON.stringify(currentData));
      
      // Import applications
      await AsyncStorage.setItem(this.STORAGE_KEYS.APPLICATIONS, JSON.stringify(importData.applications));
      
      // Import notifications if available
      if (importData.notifications) {
        await AsyncStorage.setItem(this.STORAGE_KEYS.NOTIFICATIONS, JSON.stringify(importData.notifications));
      }
      
      // Update analytics
      await this.updateAnalytics();
      
      console.log('✅ Applications data imported successfully');
      return true;
    } catch (error) {
      console.error('❌ Error importing applications data:', error);
      return false;
    }
  }
}

export default UnifiedJobApplicationService;
